import React, { useState } from "react"
import HeaderSection from "../../components/header-section"
import Sidebar from "../../features/sidebar/components/sidebar"
import {
  useGetAdminTeamSimulationsQuery,
  useLazyGetAdminTeamSimulationDetailsQuery,
  type AdminTeamSimulationItem,
  type TeamSimulationDetailsData,
} from "../../app/services/meseeknaApiV2"
import { HiOutlineUserGroup, HiOutlineOfficeBuilding, HiOutlineSearch, HiOutlineX, HiOutlineClipboardCopy, HiOutlineDownload, HiOutlineChevronDown, HiOutlineChevronRight } from "react-icons/hi"
import { BiRefresh } from "react-icons/bi"
import { FiUsers, FiMail, FiClock, FiCheck, FiXCircle, FiMessageCircle } from "react-icons/fi"
import { MdOutlineTimer } from "react-icons/md"

// Icon wrapper components for TypeScript compatibility
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const UserGroupIcon = (props: { size?: number; className?: string }): JSX.Element => <HiOutlineUserGroup {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const OfficeBuildingIcon = (props: { size?: number; className?: string }): JSX.Element => <HiOutlineOfficeBuilding {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const SearchIcon = (props: { size?: number; className?: string }): JSX.Element => <HiOutlineSearch {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const CloseIcon = (props: { size?: number; className?: string }): JSX.Element => <HiOutlineX {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const CopyIcon = (props: { size?: number; className?: string }): JSX.Element => <HiOutlineClipboardCopy {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const DownloadIcon = (props: { size?: number; className?: string }): JSX.Element => <HiOutlineDownload {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const ChevronDownIcon = (props: { size?: number; className?: string }): JSX.Element => <HiOutlineChevronDown {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const ChevronRightIcon = (props: { size?: number; className?: string }): JSX.Element => <HiOutlineChevronRight {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const RefreshIcon = (props: { size?: number; className?: string }): JSX.Element => <BiRefresh {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const UsersIcon = (props: { size?: number; className?: string }): JSX.Element => <FiUsers {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const MailIcon = (props: { size?: number; className?: string }): JSX.Element => <FiMail {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const ClockIcon = (props: { size?: number; className?: string }): JSX.Element => <FiClock {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const CheckIcon = (props: { size?: number; className?: string }): JSX.Element => <FiCheck {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const XCircleIcon = (props: { size?: number; className?: string }): JSX.Element => <FiXCircle {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const MessageIcon = (props: { size?: number; className?: string }): JSX.Element => <FiMessageCircle {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const TimerIcon = (props: { size?: number; className?: string }): JSX.Element => <MdOutlineTimer {...props} />

type StatusFilter = "all" | "pending" | "active" | "completed" | "cancelled"

const formatDate = (dateString: string | null): string => {
  if (!dateString) return "-"
  const date = new Date(dateString)
  return date.toLocaleString()
}

const getStatusBadge = (status: string) => {
  const styles: Record<string, string> = {
    pending: "bg-amber-100 text-amber-800",
    active: "bg-green-100 text-green-800",
    completed: "bg-blue-100 text-blue-800",
    cancelled: "bg-red-100 text-red-800",
  }
  return styles[status] || "bg-gray-100 text-gray-800"
}

const getVotingStatusBadge = (status: string) => {
  const styles: Record<string, string> = {
    pending: "bg-amber-100 text-amber-800",
    approved: "bg-green-100 text-green-800",
    rejected: "bg-red-100 text-red-800",
  }
  return styles[status] || "bg-gray-100 text-gray-800"
}

// Modal for viewing JSON details
const JsonViewerModal: React.FC<{
  isOpen: boolean
  onClose: () => void
  data: TeamSimulationDetailsData | null
  isLoading: boolean
}> = ({ isOpen, onClose, data, isLoading }) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(["overview", "participants", "choices"])
  )

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev)
      if (next.has(section)) {
        next.delete(section)
      } else {
        next.add(section)
      }
      return next
    })
  }

  const copyToClipboard = () => {
    if (data) {
      navigator.clipboard.writeText(JSON.stringify(data, null, 2))
    }
  }

  const downloadJson = () => {
    if (data) {
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json",
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = `team-simulation-${data.teamSimulation.id}.json`
      a.click()
      URL.revokeObjectURL(url)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col m-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Team Simulation Details
            </h2>
            {data && (
              <p className="text-sm text-gray-500 mt-1">
                {data.teamSimulation.name} - {data.teamSimulation.organizationName}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={copyToClipboard}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
              title="Copy JSON to clipboard"
            >
              <CopyIcon size={20} />
            </button>
            <button
              onClick={downloadJson}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
              title="Download JSON"
            >
              <DownloadIcon size={20} />
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              <CloseIcon size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : data ? (
            <div className="space-y-4">
              {/* Overview Section */}
              <div className="border border-gray-200 rounded-lg">
                <button
                  onClick={() => toggleSection("overview")}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50"
                >
                  <div className="flex items-center gap-2 font-medium text-gray-900">
                    {expandedSections.has("overview") ? (
                      <ChevronDownIcon size={16} />
                    ) : (
                      <ChevronRightIcon size={16} />
                    )}
                    Overview
                  </div>
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(
                      data.teamSimulation.status
                    )}`}
                  >
                    {data.teamSimulation.status}
                  </span>
                </button>
                {expandedSections.has("overview") && (
                  <div className="px-4 pb-4 grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">ID</p>
                      <p className="font-mono text-xs break-all">{data.teamSimulation.id}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Passcode</p>
                      <p className="font-mono font-bold">{data.teamSimulation.passcode}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Scenario</p>
                      <p>{data.teamSimulation.scenarioName || "Unknown"}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Organization</p>
                      <p>{data.teamSimulation.organizationName}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Created By</p>
                      <p>{data.teamSimulation.createdByEmail || "Unknown"}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Created At</p>
                      <p>{formatDate(data.teamSimulation.createdAt)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Started At</p>
                      <p>{formatDate(data.teamSimulation.startedAt)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Ended At</p>
                      <p>{formatDate(data.teamSimulation.endedAt)}</p>
                    </div>
                    {data.teamSimulation.scheduledStartTime && (
                      <>
                        <div>
                          <p className="text-gray-500">Scheduled Start</p>
                          <p>{formatDate(data.teamSimulation.scheduledStartTime)}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Timezone</p>
                          <p>{data.teamSimulation.timezone || "N/A"}</p>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Collaboration Room Section */}
              {data.collaborationRoom && (
                <div className="border border-gray-200 rounded-lg">
                  <button
                    onClick={() => toggleSection("room")}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50"
                  >
                    <div className="flex items-center gap-2 font-medium text-gray-900">
                      {expandedSections.has("room") ? (
                        <ChevronDownIcon size={16} />
                      ) : (
                        <ChevronRightIcon size={16} />
                      )}
                      Collaboration Room
                    </div>
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(
                        data.collaborationRoom.status
                      )}`}
                    >
                      {data.collaborationRoom.status}
                    </span>
                  </button>
                  {expandedSections.has("room") && (
                    <div className="px-4 pb-4 grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500">Room ID</p>
                        <p className="font-mono text-xs break-all">{data.collaborationRoom.id}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Voting Threshold</p>
                        <p>{data.collaborationRoom.votingThreshold}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Time Limit</p>
                        <p>{data.collaborationRoom.votingTimeLimit}s</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Participants</p>
                        <p>
                          {data.collaborationRoom.currentParticipants} /{" "}
                          {data.collaborationRoom.maxParticipants}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Participants Section */}
              <div className="border border-gray-200 rounded-lg">
                <button
                  onClick={() => toggleSection("participants")}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50"
                >
                  <div className="flex items-center gap-2 font-medium text-gray-900">
                    {expandedSections.has("participants") ? (
                      <ChevronDownIcon size={16} />
                    ) : (
                      <ChevronRightIcon size={16} />
                    )}
                    Participants
                  </div>
                  <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-medium">
                    {data.participants.length}
                  </span>
                </button>
                {expandedSections.has("participants") && (
                  <div className="px-4 pb-4">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-gray-500">
                          <th className="py-2 pr-4">Email</th>
                          <th className="py-2 pr-4">Name</th>
                          <th className="py-2 pr-4">Status</th>
                          <th className="py-2 pr-4">Joined</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.participants.map((p) => (
                          <tr key={p.id} className="border-t border-gray-100">
                            <td className="py-2 pr-4">{p.email}</td>
                            <td className="py-2 pr-4">
                              {p.firstName || p.lastName
                                ? `${p.firstName || ""} ${p.lastName || ""}`.trim()
                                : "-"}
                            </td>
                            <td className="py-2 pr-4">
                              <span
                                className={`px-2 py-0.5 rounded-full text-xs ${getStatusBadge(
                                  p.status
                                )}`}
                              >
                                {p.status}
                              </span>
                            </td>
                            <td className="py-2 pr-4 text-xs">
                              {formatDate(p.joinedAt)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Choices Section */}
              <div className="border border-gray-200 rounded-lg">
                <button
                  onClick={() => toggleSection("choices")}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50"
                >
                  <div className="flex items-center gap-2 font-medium text-gray-900">
                    {expandedSections.has("choices") ? (
                      <ChevronDownIcon size={16} />
                    ) : (
                      <ChevronRightIcon size={16} />
                    )}
                    Choices & Votes
                  </div>
                  <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-medium">
                    {data.choices.length}
                  </span>
                </button>
                {expandedSections.has("choices") && (
                  <div className="px-4 pb-4 space-y-4">
                    {data.choices.length === 0 ? (
                      <p className="text-gray-500 text-sm py-4 text-center">
                        No choices recorded
                      </p>
                    ) : (
                      data.choices.map((choice, idx) => (
                        <div
                          key={choice.id}
                          className="border border-gray-100 rounded-lg p-3 bg-gray-50"
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <p className="font-medium text-sm">
                                #{idx + 1}: {choice.choiceText}
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                Type: {choice.choiceType} | ID: {choice.choiceId}
                              </p>
                              {choice.proposedBy && (
                                <p className="text-xs text-gray-500">
                                  Proposed by: {choice.proposedBy.email}
                                </p>
                              )}
                            </div>
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium ${getVotingStatusBadge(
                                choice.votingStatus
                              )}`}
                            >
                              {choice.votingStatus}
                              {choice.selectedBySimulation && " (Selected)"}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 text-xs text-gray-600">
                            <span className="flex items-center gap-1">
                              <CheckIcon size={12} className="text-green-500" />
                              For: {choice.votesFor}
                            </span>
                            <span className="flex items-center gap-1">
                              <XCircleIcon size={12} className="text-red-500" />
                              Against: {choice.votesAgainst}
                            </span>
                            <span>Abstain: {choice.votesAbstain}</span>
                            <span>Required: {choice.requiredVotes}</span>
                          </div>
                          {choice.votes.length > 0 && (
                            <div className="mt-2 pt-2 border-t border-gray-200">
                              <p className="text-xs font-medium text-gray-600 mb-1">
                                Individual Votes:
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {choice.votes.map((vote) => (
                                  <span
                                    key={vote.participantId}
                                    className={`text-xs px-2 py-0.5 rounded ${
                                      vote.decision === "approve"
                                        ? "bg-green-100 text-green-700"
                                        : vote.decision === "reject"
                                        ? "bg-red-100 text-red-700"
                                        : "bg-gray-100 text-gray-700"
                                    }`}
                                  >
                                    {vote.participantEmail}: {vote.decision}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>

              {/* Chat Messages Section */}
              <div className="border border-gray-200 rounded-lg">
                <button
                  onClick={() => toggleSection("chat")}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50"
                >
                  <div className="flex items-center gap-2 font-medium text-gray-900">
                    {expandedSections.has("chat") ? (
                      <ChevronDownIcon size={16} />
                    ) : (
                      <ChevronRightIcon size={16} />
                    )}
                    Chat Messages
                  </div>
                  <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-medium">
                    {data.chatMessages.length}
                  </span>
                </button>
                {expandedSections.has("chat") && (
                  <div className="px-4 pb-4 max-h-64 overflow-y-auto">
                    {data.chatMessages.length === 0 ? (
                      <p className="text-gray-500 text-sm py-4 text-center">
                        No chat messages
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {data.chatMessages.map((msg) => (
                          <div
                            key={msg.id}
                            className="flex items-start gap-2 text-sm py-1"
                          >
                            <MessageIcon size={14} className="text-gray-400 mt-0.5" />
                            <div className="flex-1">
                              <span className="font-medium text-gray-900">
                                {msg.senderName}:
                              </span>{" "}
                              <span className="text-gray-700">{msg.message}</span>
                              <p className="text-xs text-gray-400">
                                {formatDate(msg.createdAt)}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Raw JSON Section */}
              <div className="border border-gray-200 rounded-lg">
                <button
                  onClick={() => toggleSection("raw")}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50"
                >
                  <div className="flex items-center gap-2 font-medium text-gray-900">
                    {expandedSections.has("raw") ? (
                      <ChevronDownIcon size={16} />
                    ) : (
                      <ChevronRightIcon size={16} />
                    )}
                    Raw JSON
                  </div>
                </button>
                {expandedSections.has("raw") && (
                  <div className="px-4 pb-4">
                    <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-xs overflow-x-auto max-h-96">
                      {JSON.stringify(data, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">
              Failed to load team simulation details
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

// Team Simulation Card Component
const TeamSimulationCard: React.FC<{
  simulation: AdminTeamSimulationItem
  onViewDetails: (id: string) => void
}> = ({ simulation, onViewDetails }) => (
  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow">
    <div className="flex items-start justify-between mb-3">
      <div className="flex items-center gap-2">
        <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
          <UserGroupIcon className="text-purple-600" size={18} />
        </div>
        <div>
          <p className="font-medium text-gray-900">{simulation.name}</p>
          <p className="text-sm text-gray-500">
            {simulation.scenarioName || "Unknown Scenario"}
          </p>
        </div>
      </div>
      <span
        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(
          simulation.status
        )}`}
      >
        {simulation.status === "active" && (
          <span className="w-2 h-2 bg-green-500 rounded-full mr-1 animate-pulse"></span>
        )}
        {simulation.status.charAt(0).toUpperCase() + simulation.status.slice(1)}
      </span>
    </div>

    <div className="space-y-2">
      <div className="flex items-center gap-2 text-sm">
        <OfficeBuildingIcon className="text-gray-400" size={14} />
        <span className="text-gray-600">{simulation.organizationName}</span>
      </div>
      <div className="flex items-center gap-2 text-sm">
        <UsersIcon className="text-gray-400" size={14} />
        <span className="text-gray-600">
          {simulation.participantCount} participant
          {simulation.participantCount !== 1 ? "s" : ""}
        </span>
      </div>
      {simulation.createdByEmail && (
        <div className="flex items-center gap-2 text-sm">
          <MailIcon className="text-gray-400" size={14} />
          <span className="text-gray-600 truncate">{simulation.createdByEmail}</span>
        </div>
      )}
      <div className="flex items-center gap-2 text-sm">
        <ClockIcon className="text-gray-400" size={14} />
        <span className="text-gray-600">
          Created: {formatDate(simulation.createdAt)}
        </span>
      </div>
      {simulation.collaborationRoom && simulation.collaborationRoom.choices.length > 0 && (
        <div className="flex items-center gap-2 text-sm">
          <CheckIcon className="text-gray-400" size={14} />
          <span className="text-gray-600">
            {simulation.collaborationRoom.choices.length} choice(s) made
          </span>
        </div>
      )}
    </div>

    <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between">
      <div className="flex items-center gap-1 text-sm text-gray-500">
        <TimerIcon size={16} />
        <span className="font-mono text-xs">{simulation.passcode}</span>
      </div>
      <button
        onClick={() => onViewDetails(simulation.id)}
        className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
      >
        View JSON
      </button>
    </div>
  </div>
)

const TeamSimulationsPage: React.FC = () => {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all")
  const [searchQuery, setSearchQuery] = useState("")
  const [selectedSimId, setSelectedSimId] = useState<string | null>(null)

  const { data, isLoading, refetch } = useGetAdminTeamSimulationsQuery({
    status: statusFilter,
    search: searchQuery || undefined,
    limit: 100,
  })

  const [getDetails, { data: detailsData, isLoading: detailsLoading }] =
    useLazyGetAdminTeamSimulationDetailsQuery()

  const teamSimulations = data?.data?.teamSimulations || []

  const handleViewDetails = (id: string) => {
    setSelectedSimId(id)
    getDetails(id)
  }

  const handleCloseModal = () => {
    setSelectedSimId(null)
  }

  const statusCounts = {
    all: teamSimulations.length,
    pending: teamSimulations.filter((s) => s.status === "pending").length,
    active: teamSimulations.filter((s) => s.status === "active").length,
    completed: teamSimulations.filter((s) => s.status === "completed").length,
    cancelled: teamSimulations.filter((s) => s.status === "cancelled").length,
  }

  return (
    <div className="flex bg-[#F9FAFB] min-h-screen w-full overflow-hidden">
      <Sidebar />
      <div className="flex flex-col flex-1 overflow-hidden">
        <HeaderSection />
        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-7xl mx-auto">
            {/* Page Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <UserGroupIcon className="text-purple-500" />
                  Team Simulations
                </h1>
                <p className="text-gray-500 mt-1">
                  View all team simulations and their JSON data
                </p>
              </div>
              <button
                onClick={() => refetch()}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <RefreshIcon size={18} />
                Refresh
              </button>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
              <div className="flex flex-wrap items-center gap-4">
                {/* Search */}
                <div className="flex-1 min-w-64">
                  <div className="relative">
                    <SearchIcon
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                      size={18}
                    />
                    <input
                      type="text"
                      placeholder="Search by name, organization, email, or scenario..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {/* Status Filter */}
                <div className="flex items-center gap-2">
                  {(["all", "pending", "active", "completed", "cancelled"] as StatusFilter[]).map(
                    (status) => (
                      <button
                        key={status}
                        onClick={() => setStatusFilter(status)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                          statusFilter === status
                            ? "bg-purple-100 text-purple-800"
                            : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                        }`}
                      >
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                        <span className="ml-1 text-xs opacity-75">
                          ({statusCounts[status]})
                        </span>
                      </button>
                    )
                  )}
                </div>
              </div>
            </div>

            {/* Results */}
            {isLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[...Array(6)].map((_, i) => (
                  <div
                    key={i}
                    className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 animate-pulse"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                      <div className="flex-1">
                        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                        <div className="h-3 bg-gray-200 rounded w-1/2 mt-1"></div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="h-3 bg-gray-200 rounded"></div>
                      <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : teamSimulations.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {teamSimulations.map((sim) => (
                  <TeamSimulationCard
                    key={sim.id}
                    simulation={sim}
                    onViewDetails={handleViewDetails}
                  />
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                <UserGroupIcon size={48} className="mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900">
                  No Team Simulations Found
                </h3>
                <p className="text-gray-500 mt-1">
                  {searchQuery
                    ? "No team simulations match your search criteria."
                    : "There are no team simulations yet."}
                </p>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* JSON Viewer Modal */}
      <JsonViewerModal
        isOpen={selectedSimId !== null}
        onClose={handleCloseModal}
        data={detailsData?.data || null}
        isLoading={detailsLoading}
      />
    </div>
  )
}

export default TeamSimulationsPage
